import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import type { ServedAd } from "@/api/types";
import { ProductCard } from "@/components/commerce/ProductCard";
import { StoreCard } from "@/components/commerce/StoreCard";
import { Rail, RailItem } from "@/components/commerce/Rail";
import { SmartImage } from "@/components/ui/SmartImage";
import { cn } from "@/lib/cn";
import { trackClick, useImpression, useServedAds } from "./api";

export function SponsoredTag({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-void/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ink-muted backdrop-blur",
        className,
      )}
    >
      <Megaphone className="size-3" aria-hidden /> Sponsored
    </span>
  );
}

/** A native listing ad: the normal card, labelled, with impression and click tracking. */
export function SponsoredProduct({ ad, layout = "grid", className }: { ad: ServedAd; layout?: "grid" | "list"; className?: string }) {
  const ref = useImpression<HTMLDivElement>(ad.token);
  if (!ad.product) return null;
  return (
    <div ref={ref} className={cn("relative h-full", className)} onClickCapture={() => trackClick(ad.token)}>
      <ProductCard product={ad.product} layout={layout} />
      <SponsoredTag className="pointer-events-none absolute bottom-2 left-2 z-10" />
    </div>
  );
}

export function SponsoredStore({ ad }: { ad: ServedAd }) {
  const ref = useImpression<HTMLDivElement>(ad.token);
  if (!ad.store) return null;
  return (
    <div ref={ref} className="relative h-full" onClickCapture={() => trackClick(ad.token)}>
      <StoreCard store={ad.store} />
      <SponsoredTag className="pointer-events-none absolute right-3 top-3" />
    </div>
  );
}

/** Image banner ad (category page). */
export function BannerAd({ ad, className }: { ad: ServedAd; className?: string }) {
  const ref = useImpression<HTMLAnchorElement>(ad.token);
  const external = ad.cta_url.startsWith("http");
  const inner = (
    <>
      <SmartImage
        src={ad.image}
        mobileSrc={ad.mobile_image}
        alt=""
        fallbackLabel={ad.headline}
        width={1200}
        maxWidth={1440}
        className="aspect-[4/1] w-full sm:aspect-[1200/300]"
      />
      <span className="absolute inset-0 bg-gradient-to-r from-void/85 via-void/40 to-transparent" aria-hidden />
      <span className="absolute inset-y-0 left-0 flex max-w-[70%] flex-col justify-center gap-1 p-4 sm:p-6">
        <span className="font-display text-lg font-bold text-ink sm:text-2xl">{ad.headline}</span>
        {ad.body && <span className="hidden text-sm text-ink-soft sm:block">{ad.body}</span>}
        {ad.cta_label && (
          <span className="mt-1 w-fit rounded-full bg-[var(--primary-strong)] px-3 py-1 text-xs font-medium text-white">
            {ad.cta_label}
          </span>
        )}
      </span>
      <SponsoredTag className="absolute right-3 top-3" />
    </>
  );
  const cls = cn("relative block overflow-hidden rounded-card border border-line", className);
  return external ? (
    <a
      ref={ref}
      href={ad.cta_url}
      target="_blank"
      rel="noopener sponsored"
      className={cls}
      onClick={() => trackClick(ad.token)}
      aria-label={`${ad.headline} (sponsored)`}
    >
      {inner}
    </a>
  ) : (
    <Link ref={ref} to={ad.cta_url} className={cls} onClick={() => trackClick(ad.token)} aria-label={`${ad.headline} (sponsored)`}>
      {inner}
    </Link>
  );
}

export function CategoryBannerAd({ category }: { category: string }) {
  const ads = useServedAds("CATEGORY_BANNER", { category, limit: 1 });
  const ad = ads.data?.[0];
  return ad ? <BannerAd ad={ad} /> : null;
}

/** "Sponsored" rail under similar items on the product page. */
export function ProductStripAds({ category, excludeStore }: { category?: string; excludeStore?: string }) {
  const ads = useServedAds("PRODUCT_STRIP", { category, limit: 6, exclude_store: excludeStore }, !!category);
  const rows = (ads.data ?? []).filter((a) => a.product);
  if (!rows.length) return null;
  return (
    <Rail title="Sponsored">
      {rows.map((ad) => (
        <RailItem key={ad.token}>
          <SponsoredProduct ad={ad} />
        </RailItem>
      ))}
    </Rail>
  );
}
